import { useCallback, useEffect, useMemo, useState } from "react";

export interface UseImageViewerOptions {
  src: string | null;
  containerWidth?: number;
  containerHeight?: number;
  initialScale?: number; // 1 = fit to container
  minScale?: number;
  maxScale?: number;
}

export interface UseImageViewerReturn {
  scale: number;
  setScale: (s: number | ((p: number) => number)) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  openInNewTab: () => void;
  computedSize: { width: number; height: number } | null;
}

export const useImageViewer = ({
  src,
  containerWidth,
  containerHeight,
  initialScale = 1,
  minScale = 0.2,
  maxScale = 6,
}: UseImageViewerOptions): UseImageViewerReturn => {
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [scale, setScale] = useState<number>(initialScale);

  // load image to get intrinsic size
  useEffect(() => {
    if (!src) return;
    const img = new Image();
    img.onload = () =>
      setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    img.src = src;
  }, [src]);

  const openInNewTab = useCallback(() => {
    if (src) window.open(src, "_blank");
  }, [src]);

  const zoomIn = useCallback(
    () => setScale((p) => Math.min(maxScale, p + 0.2)),
    [maxScale],
  );
  const zoomOut = useCallback(
    () => setScale((p) => Math.max(minScale, p - 0.2)),
    [minScale],
  );
  const resetZoom = useCallback(() => setScale(initialScale), [initialScale]);

  // Compute fitted size to fill container while preserving aspect ratio, then apply scale
  const computedSize = useMemo(() => {
    if (
      !naturalSize ||
      containerWidth === undefined ||
      containerHeight === undefined
    )
      return null;
    const clientWidth = containerWidth;
    const clientHeight = containerHeight;
    if (clientWidth <= 0 || clientHeight <= 0) return null;

    const containerRatio = clientWidth / clientHeight;
    const imageRatio = naturalSize.width / naturalSize.height;

    let width: number;
    let height: number;
    if (imageRatio > containerRatio) {
      width = clientWidth;
      height = Math.round(width / imageRatio);
    } else {
      height = clientHeight;
      width = Math.round(height * imageRatio);
    }

    return {
      width: Math.round(width * scale),
      height: Math.round(height * scale),
    };
  }, [containerWidth, containerHeight, naturalSize, scale]);

  return {
    scale,
    setScale,
    zoomIn,
    zoomOut,
    resetZoom,
    openInNewTab,
    computedSize,
  };
};
